import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/axios';
import { formatAppDateTime } from '../utils/dateFormat';
import { normalizePhoneNumber } from '../utils/phoneUtils';
import { uniqueTenantMembers, matchesSsoRecruiter } from '../utils/recruiterSsoFilter';

const AVATAR_COLORS = ['#0041C2','#10B981','#7C3AED','#F59E0B','#EF4444','#06B6D4','#EC4899','#059669','#D97706'];

const getInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getAvatarColor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

/** Compact page list: e.g. [1, 'ellipsis', 50, 51, 52, 'ellipsis', 87] */
function getPaginationItems(totalPages, currentPage, siblingCount = 1) {
  if (totalPages <= 1) return [1];

  const maxVisible = siblingCount * 2 + 5;
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(currentPage - siblingCount, 1);
  const rightSibling = Math.min(currentPage + siblingCount, totalPages);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftRange = Array.from({ length: 3 + 2 * siblingCount }, (_, i) => i + 1);
    return [...leftRange, 'ellipsis', totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightRange = Array.from(
      { length: 3 + 2 * siblingCount },
      (_, i) => totalPages - (3 + 2 * siblingCount) + i + 1
    );
    return [1, 'ellipsis', ...rightRange];
  }

  if (showLeftEllipsis && showRightEllipsis) {
    const middleRange = Array.from(
      { length: rightSibling - leftSibling + 1 },
      (_, i) => leftSibling + i
    );
    return [1, 'ellipsis', ...middleRange, 'ellipsis', totalPages];
  }

  return Array.from({ length: totalPages }, (_, i) => i + 1);
}

const STATUS_CFG = {
  Interested:        { label: 'Interested',      dot: '#10B981', bg: 'rgba(16,185,129,.1)',   color: '#059669', border: 'rgba(16,185,129,.2)',  online: true  },
  Shortlisted:       { label: 'Shortlisted',     dot: '#7C3AED', bg: 'rgba(124,58,237,.1)',   color: '#6D28D9', border: 'rgba(124,58,237,.2)',  online: true  },
  'Not Shortlisted': { label: 'Not Shortlisted', dot: '#F97316', bg: 'rgba(249,115,22,.1)',   color: '#EA580C', border: 'rgba(249,115,22,.2)',  online: false },
  'Not Interested':  { label: 'Not Interested',  dot: '#EF4444', bg: 'rgba(239,68,68,.1)',   color: '#DC2626', border: 'rgba(239,68,68,.15)',  online: false },
  'Not Responded':   { label: 'Not Responded',   dot: '#8A96B0', bg: 'rgba(139,144,160,.1)', color: '#8A96B0', border: 'rgba(139,144,160,.2)', online: false },
};

// Synced with dashboard: interested+completed+asking-q*+awaiting-details all count as Interested.
// sent/failed/delivered with no Yes/No reply count as Not Responded (No Response chip).
const normalizeStatus = (record) => {
  const raw = (record?.status || '').toLowerCase();
  if (raw === 'interested' || raw === 'completed' || /^asking-q\d+$/.test(raw) || raw === 'awaiting-details') return 'Interested';
  if (raw === 'shortlisted') return 'Shortlisted';
  if (raw === 'not-shortlisted') return 'Not Shortlisted';
  if (raw === 'not-interested' || raw === 'not_interested') return 'Not Interested';
  return 'Not Responded';
};

const STATUS_PRIORITY = {
  Shortlisted: 6,
  Interested: 5,
  'Not Shortlisted': 4,
  'Not Interested': 4,
  Failed: 2,
  'Not Responded': 1,
};

const mergeConversationStatus = (current, apiStatus) => {
  if (!apiStatus) return current;
  const fromApi = normalizeStatus({ status: apiStatus });
  const curP = STATUS_PRIORITY[current] ?? 1;
  const apiP = STATUS_PRIORITY[fromApi] ?? 1;
  return apiP >= curP ? fromApi : current;
};

const toTs = (v) => { const d = new Date(v); return isNaN(d.getTime()) ? null : d; };

const deriveLastContacted = (rec) =>
  rec?.messageType === 'user_reply'
    ? rec.respondedAt || rec.updatedAt || rec.createdAt || null
    : rec?.respondedAt || rec?.updatedAt || rec?.deliveredAt || null;

const mapConvMsgs = (messages = []) =>
  messages
    .map((m) => ({ direction: m.sender === 'candidate' ? 'incoming' : 'outgoing', text: m.message, time: m.timestamp }))
    .filter((m) => m.text);

const parseSkills = (rec) => {
  const raw = rec?.skills || rec?.skillSet || rec?.technicalSkills || rec?.matchingSkills || '';
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  if (typeof raw === 'string' && raw.trim()) return raw.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
};

const fmtDateTime = (value) => {
  if (!value) return '-';
  const formatted = formatAppDateTime(value);
  return formatted || '-';
};

const fmtTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit' }).format(d);
};

const iconActionBtn = {
  width: 24, height: 24, borderRadius: 6,
  border: '1px solid var(--ds-border)',
  background: 'transparent', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', flexShrink: 0, padding: 0,
};

const chatHdrBtn = {
  width: 30, height: 30, borderRadius: 8,
  border: '1px solid var(--ds-border)',
  background: 'transparent', display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', padding: 0,
};

const chatActBtn = {
  display: 'flex', alignItems: 'center', gap: 4,
  padding: '5px 10px', borderRadius: 6,
  border: '1px solid var(--ds-border)',
  background: 'transparent', color: 'var(--ds-text2)',
  fontSize: 11, fontFamily: 'inherit', fontWeight: 500, cursor: 'pointer',
};

const selectStyle = {
  background: 'var(--ds-input-bg)', border: '1px solid var(--ds-border)',
  borderRadius: 8, padding: '7px 28px 7px 12px', fontSize: 12,
  color: 'var(--ds-text)', outline: 'none', appearance: 'none',
  cursor: 'pointer', fontFamily: 'inherit',
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%238A96B0' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
};

const StatusBadge = ({ status, small = false }) => {
  const c = STATUS_CFG[status] || STATUS_CFG['Not Responded'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: small ? '1px 7px' : '3px 10px',
      borderRadius: 20, fontSize: small ? 10 : 11,
      fontWeight: 600, whiteSpace: 'nowrap',
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {c.label}
    </span>
  );
};

const PAGE_SIZE = 8;

const CandidateEngagement = () => {
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [candidates, setCandidates] = useState([]);
  const [positions, setPositions]   = useState([]);
  const [tenantMembers, setTenantMembers] = useState([]);
  const [filterPosition, setFilterPosition] = useState('');
  const [filterRecruiter, setFilterRecruiter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy]         = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const [selId, setSelId]           = useState(null);
  const [chatMsgs, setChatMsgs]         = useState([]);
  const [chatLoading, setChatLoading]   = useState(false);
  const [chatError, setChatError]       = useState('');
  const [chatRefreshing, setChatRefreshing] = useState(false);
  const [chatInput, setChatInput]       = useState('');
  const [sendingChat, setSendingChat]   = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedCandids, setSelectedCandids] = useState(new Set());
  const [bulkSending, setBulkSending]   = useState(false);
  const chatScrollRef  = useRef(null);
  const selIdRef       = useRef(null);
  const candidatesRef  = useRef([]);

  const loadCandidates = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [posRes, tenantRes] = await Promise.all([
        api.get('/positions'),
        api.get('/analytics/tenants').catch(() => ({ data: {} })),
      ]);
      const posArr = Array.isArray(posRes.data) ? posRes.data : [];
      setPositions(posArr);
      setTenantMembers(Array.isArray(tenantRes.data?.members) ? tenantRes.data.members : []);
      const titleById        = new Map(posArr.map((p) => [p.positionId ?? p.id, p.positionName || p.title || 'N/A']));
      const jobCodeById      = new Map(posArr.map((p) => [p.positionId ?? p.id, p.jobCode || '']));
      const recruiterById    = new Map(posArr.map((p) => [p.positionId ?? p.id, p.createdByEmail || '']));
      const recruiterNameById = new Map(posArr.map((p) => [p.positionId ?? p.id, p.createdBy || p.createdByEmail || '']));
      const msgResps = await Promise.allSettled(
        posArr.map((p) => p.positionId ?? p.id).filter(Boolean).map((id) => api.get(`/whatsapp-messages/position/${id}`))
      );
      const allRecs = msgResps.filter((r) => r.status === 'fulfilled').flatMap((r) => Array.isArray(r.value.data) ? r.value.data : []);
      const map = new Map();
      allRecs.forEach((rec) => {
        const phone = rec.phoneNumber || '';
        const posId = rec.positionId  || '';
        const phoneKey = normalizePhoneNumber(phone) || phone.trim();
        const key   = `${phoneKey}-${posId}`;
        const lc    = deriveLastContacted(rec);
        const ex    = map.get(key);
        if (!ex) {
          map.set(key, {
            id: key, phoneNumber: phoneKey || phone, positionId: posId,
            name: rec.candidateName || 'Unknown Candidate',
            role: rec.jobTitle || titleById.get(posId) || 'N/A',
            jobCode: jobCodeById.get(posId) || '',
            recruiterEmail: recruiterById.get(posId) || '',
            recruiterName: recruiterNameById.get(posId) || '',
            status: normalizeStatus(rec), lastContacted: lc, statusDate: lc,
            shortlistStatus: rec.shortlistStatus || null,
            criteriaRedFlag: !!(rec.criteriaRedFlag || rec.criteria_red_flag),
            criteriaNoCount: rec.criteriaNoCount ?? rec.criteria_no_count ?? null,
            criteriaTotalCount: rec.criteriaTotalCount ?? rec.criteria_total_count ?? null,
            skills: parseSkills(rec),
            sourceMessageIds: rec?.id ? [rec.id] : [],
          });
        } else {
          const exTs  = toTs(ex.statusDate)?.getTime() || 0;
          const recTs = toTs(lc)?.getTime() || 0;
          map.set(key, {
            ...ex,
            role: ex.role === 'N/A' ? (rec.jobTitle || ex.role) : ex.role,
            status: mergeConversationStatus(ex.status, rec.status),
            shortlistStatus: rec.shortlistStatus || ex.shortlistStatus || null,
            criteriaRedFlag: !!(rec.criteriaRedFlag || rec.criteria_red_flag) || !!ex.criteriaRedFlag,
            criteriaNoCount: rec.criteriaNoCount ?? rec.criteria_no_count ?? ex.criteriaNoCount,
            criteriaTotalCount: rec.criteriaTotalCount ?? rec.criteria_total_count ?? ex.criteriaTotalCount,
            statusDate:    recTs >= exTs ? lc : ex.statusDate,
            lastContacted: recTs > exTs ? lc : ex.lastContacted,
            skills: Array.from(new Set([...ex.skills, ...parseSkills(rec)])),
            sourceMessageIds: Array.from(new Set([...ex.sourceMessageIds, ...(rec?.id ? [rec.id] : [])])),
          });
        }
      });
      const sorted = Array.from(map.values()).sort((a, b) => new Date(b.lastContacted || 0) - new Date(a.lastContacted || 0));
      setCandidates(sorted);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load candidate engagement data.');
      setCandidates([]);
      setTenantMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  // Keep refs in sync so the chat interval can read latest values without being a dependency
  useEffect(() => { selIdRef.current = selId; }, [selId]);
  useEffect(() => { candidatesRef.current = candidates; }, [candidates]);

  // Fetch latest chat messages for the open conversation (silent, no loading state)
  const fetchChatSilent = useCallback(async (showSpinner = false) => {
    const id   = selIdRef.current;
    const cand = candidatesRef.current.find((c) => c.id === id);
    if (!id || !cand?.phoneNumber || cand.positionId == null) return;
    if (showSpinner) setChatRefreshing(true);
    try {
      const res = await api.get('/whatsapp-messages/conversation', {
        params: { phoneNumber: cand.phoneNumber, positionId: Number(cand.positionId) },
      });
      const pl = res.data || {};
      const newMsgs = mapConvMsgs(pl.messages);
      setChatMsgs((prev) => {
        // Only update if content actually changed, to avoid unnecessary re-renders
        if (JSON.stringify(prev) === JSON.stringify(newMsgs)) return prev;
        // Scroll to bottom only if already near bottom
        const el = chatScrollRef.current;
        const wasAtBottom = el ? (el.scrollHeight - el.scrollTop - el.clientHeight < 80) : true;
        if (wasAtBottom) setTimeout(() => { if (el) el.scrollTop = el.scrollHeight; }, 0);
        return newMsgs;
      });
      setCandidates((prev) => prev.map((item) =>
        item.id === id
          ? { ...item, status: mergeConversationStatus(item.status, pl.status), lastContacted: pl.lastContacted || item.lastContacted }
          : item
      ));
    } catch (_) {}
    finally { if (showSpinner) setChatRefreshing(false); }
  }, []);

  // Auto-refresh chat every 3s when a conversation is open — stable interval, no candidates dep
  useEffect(() => {
    if (!selId) return;
    const t = setInterval(() => fetchChatSilent(), 3000);
    return () => clearInterval(t);
  }, [selId, fetchChatSilent]);

  // Scroll to bottom when chat first loads (chatLoading goes false after initial fetch)
  useEffect(() => {
    if (chatLoading) return;
    const el = chatScrollRef.current;
    if (el) setTimeout(() => { el.scrollTop = el.scrollHeight; }, 0);
  }, [chatLoading]);

  const recruiters = useMemo(() => uniqueTenantMembers(tenantMembers), [tenantMembers]);

  // candidates filtered by position + recruiter (for stat chip counts)
  const posFiltered = useMemo(() => {
    let r = candidates;
    if (filterPosition)  r = r.filter((c) => String(c.positionId) === String(filterPosition));
    if (filterRecruiter) r = r.filter((c) => matchesSsoRecruiter(c, filterRecruiter));
    return r;
  }, [candidates, filterPosition, filterRecruiter]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let r = posFiltered.filter((c) => {
      const mQ = !q || c.name.toLowerCase().includes(q) || c.role.toLowerCase().includes(q) || (c.jobCode || '').toLowerCase().includes(q);
      const mS = !filterStatus ||
        (filterStatus === 'Shortlisted'     ? c.shortlistStatus === 'shortlisted' :
         filterStatus === 'Not Shortlisted' ? c.shortlistStatus === 'not-shortlisted' :
         c.status === filterStatus);
      return mQ && mS;
    });
    if (sortBy === 'name')   r = [...r].sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === 'status') r = [...r].sort((a, b) => a.status.localeCompare(b.status));
    return r;
  }, [posFiltered, searchTerm, filterStatus, sortBy]);

  const statCounts = useMemo(() => ({
    Interested:        posFiltered.filter((c) => c.status === 'Interested').length,
    'Not Interested':  posFiltered.filter((c) => c.status === 'Not Interested').length,
    Shortlisted:       posFiltered.filter((c) => c.shortlistStatus === 'shortlisted').length,
    'Not Shortlisted': posFiltered.filter((c) => c.shortlistStatus === 'not-shortlisted').length,
    'Not Responded':   posFiltered.filter((c) => c.status !== 'Interested' && c.status !== 'Not Interested').length,
  }), [posFiltered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(currentPage, totalPages);
  const pageRows   = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const selCand    = useMemo(() => candidates.find((c) => c.id === selId) || null, [candidates, selId]);
  const chatOpen   = selId !== null;

  const handleSearch         = (v) => { setSearchTerm(v); setCurrentPage(1); };
  const handleStatFilter     = (s) => { setFilterStatus(s); setCurrentPage(1); setSelectedCandids(new Set()); };
  const handlePositionFilter = (v) => { setFilterPosition(v); setFilterStatus(''); setCurrentPage(1); setSelectedCandids(new Set()); handleCloseChat(); };
  const handleRecruiterFilter = (v) => { setFilterRecruiter(v); setFilterStatus(''); setCurrentPage(1); setSelectedCandids(new Set()); };

  const toggleSelectCand = (id) => {
    setSelectedCandids((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCandids.size >= filtered.length) {
      setSelectedCandids(new Set());
    } else {
      setSelectedCandids(new Set(filtered.map((c) => c.id)));
    }
  };

  const handleBulkResend = async () => {
    if (!selectedCandids.size) return;
    const toSend = filtered.filter((c) => selectedCandids.has(c.id));
    const count = toSend.length;
    setBulkSending(true);
    try {
      const res = await api.post('/whatsapp-messages/bulk-resend', {
        candidates: toSend.map((c) => ({
          phoneNumber: c.phoneNumber,
          candidateName: c.name,
          positionId: Number(c.positionId),
          jobTitle: c.role,
        })),
      });
      const { sent = 0, failed = 0 } = res.data || {};
      toast.success(`Resent to ${sent} candidate${sent !== 1 ? 's' : ''}${failed ? `, ${failed} failed` : ''}`);
      setSelectedCandids(new Set());
    } catch (err) {
      toast.error(
        count === 1
          ? 'Resend failed. Please try again.'
          : 'Bulk resend failed. Please try again.'
      );
    } finally {
      setBulkSending(false);
    }
  };

  const handleChatSend = async () => {
    const msg = chatInput.trim();
    if (!msg || !selCand || sendingChat) return;
    setSendingChat(true);
    try {
      await api.post('/whatsapp/send', { recipientNumber: selCand.phoneNumber, message: msg });
      setChatInput('');
      setChatMsgs((prev) => [...prev, { direction: 'outgoing', text: msg, time: new Date().toISOString() }]);
      setTimeout(() => { const el = chatScrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, 0);
    } catch (err) {
      toast.error('Failed to send message via WhatsApp.');
    } finally {
      setSendingChat(false);
    }
  };

  const handleChatOpen = async (cand) => {
    if (!cand?.phoneNumber || cand.positionId === '' || cand.positionId == null) {
      toast.error('Missing phone number or position for this candidate.');
      return;
    }
    setSelId(cand.id);
    setChatError(''); setChatMsgs([]); setChatLoading(true);
    try {
      const res = await api.get('/whatsapp-messages/conversation', {
        params: { phoneNumber: cand.phoneNumber, positionId: Number(cand.positionId) },
      });
      const pl   = res.data || {};
      const msgs = mapConvMsgs(pl.messages);
      setChatMsgs(msgs);
      setCandidates((prev) => prev.map((item) =>
        item.id === cand.id
          ? { ...item, status: mergeConversationStatus(item.status, pl.status), lastContacted: pl.lastContacted || item.lastContacted }
          : item
      ));
    } catch (err) {
      setChatError(err?.response?.data?.message || 'Failed to load conversation history.');
    } finally {
      setChatLoading(false);
    }
  };

  const handleCloseChat = () => { setSelId(null); setChatMsgs([]); setChatError(''); setChatInput(''); };

  const handleStatusUpdate = async (rawStatus) => {
    if (!selCand?.sourceMessageIds?.length) {
      toast.error('No backend records found to update.');
      return;
    }
    const isShortlistAction = rawStatus === 'shortlisted' || rawStatus === 'not-shortlisted';
    setUpdatingStatus(true);
    try {
      await Promise.all(
        selCand.sourceMessageIds.map((id) =>
          isShortlistAction
            ? api.patch(`/whatsapp-messages/${id}/shortlist-status`, { shortlistStatus: rawStatus })
            : api.patch(`/whatsapp-messages/${id}/status`, { status: rawStatus })
        )
      );
      const displayStatus = normalizeStatus({ status: rawStatus });
      setCandidates((prev) =>
        prev.map((c) => {
          if (c.id !== selCand.id) return c;
          return isShortlistAction
            ? { ...c, shortlistStatus: rawStatus }
            : { ...c, status: displayStatus };
        })
      );
      toast.success(`${selCand.name} marked as ${displayStatus}`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <>
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <div style={{ display: 'flex', height: 'calc(100vh - 92px)', overflow: 'hidden', margin: '-20px -24px 0', background: 'var(--ds-bg)' }}>

      {/* ── Left panel: candidate list ── */}
      <div style={{
        flex: chatOpen ? '0 0 520px' : 1,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        transition: 'flex 0.35s cubic-bezier(.4,0,.2,1)',
        borderRight: chatOpen ? '1px solid var(--ds-border)' : 'none',
      }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg,#0041C2,#1A5FD4)',
          padding: '8px 20px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>Candidate Engagement</div>
        </div>

        {/* Search + Refresh bar */}
        <div style={{ padding: '6px 20px', flexShrink: 0, background: 'var(--ds-card)', borderBottom: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--ds-bg)', border: '1.5px solid var(--ds-border)',
            borderRadius: 7, padding: '5px 10px',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="1.7" strokeLinecap="round" style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              placeholder="Search by name, role or job ID…"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 13, fontFamily: 'inherit', color: 'var(--ds-text)', width: '100%' }}
            />
            {searchTerm && (
              <button onClick={() => handleSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-text2)', padding: 0, lineHeight: 1, fontSize: 14 }}>✕</button>
            )}
          </div>
          <button
            title="Refresh candidate list"
            onClick={loadCandidates}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px',
              borderRadius: 7, background: loading ? 'var(--ds-bg)' : '#0041C2',
              border: '1.5px solid ' + (loading ? 'var(--ds-border)' : '#0041C2'),
              color: loading ? 'var(--ds-text2)' : '#fff',
              fontSize: 12, fontFamily: 'inherit', fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer', flexShrink: 0,
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
              style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }}>
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Filter bar: Position + User (SSO) */}
        <div style={{ padding: '5px 20px', flexShrink: 0, background: 'var(--ds-bg)', borderBottom: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="1.7" strokeLinecap="round" style={{ flexShrink: 0 }}>
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          <select
            value={filterPosition}
            onChange={(e) => handlePositionFilter(e.target.value)}
            style={{ ...selectStyle, flex: 1, minWidth: 160, maxWidth: 280 }}
          >
            <option value="">All Positions</option>
            {positions.map((p) => {
              const pid = p.positionId ?? p.id;
              const cnt = candidates.filter((c) => String(c.positionId) === String(pid)).length;
              return (
                <option key={pid} value={pid}>
                  {p.positionName || p.title || 'Untitled'}{p.jobCode ? ` · ${p.jobCode}` : ''} ({cnt})
                </option>
              );
            })}
          </select>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="1.7" strokeLinecap="round" style={{ flexShrink: 0 }}>
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
          </svg>
          <select
            value={filterRecruiter}
            onChange={(e) => handleRecruiterFilter(e.target.value)}
            style={{ ...selectStyle, flex: 1, minWidth: 140, maxWidth: 220 }}
          >
            <option value="">All Users</option>
            {recruiters.map(({ email, name }) => (
              <option key={email} value={email}>{name}</option>
            ))}
          </select>
          {(filterPosition || filterRecruiter) && (
            <button
              onClick={() => { handlePositionFilter(''); handleRecruiterFilter(''); }}
              style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--ds-border)', background: 'transparent', color: 'var(--ds-text2)', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              ✕ Clear
            </button>
          )}
        </div>

        {/* Stat chips — grouped by perspective */}
        <div style={{ padding: '5px 20px 0', flexShrink: 0, background: 'var(--ds-bg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {[
              { key: 'Interested',    label: 'Interested',    count: statCounts.Interested,       dot: '#10B981' },
              { key: 'Not Interested',label: 'Not Interested',count: statCounts['Not Interested'],dot: '#EF4444' },
            ].map((chip) => {
              const active = filterStatus === chip.key;
              return (
                <div key={chip.key} onClick={() => handleStatFilter(active ? '' : chip.key)}
                  style={{ background: active ? 'rgba(0,65,194,.1)' : 'var(--ds-card)', border: active ? '1px solid #0041C2' : '1px solid var(--ds-border)', borderRadius: 6, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', boxShadow: 'var(--ds-shadow)', transition: 'all .18s' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: chip.dot, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: 'var(--ds-text2)', fontWeight: 500 }}>{chip.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: active ? '#0041C2' : 'var(--ds-text)' }}>{chip.count}</span>
                </div>
              );
            })}
            <div style={{ width: 1, height: 18, background: 'var(--ds-border)', flexShrink: 0 }} />
            {[
              { key: 'Shortlisted',    label: 'Shortlisted',    count: statCounts.Shortlisted,       dot: '#7C3AED' },
              { key: 'Not Shortlisted',label: 'Not Shortlisted',count: statCounts['Not Shortlisted'],dot: '#F97316' },
            ].map((chip) => {
              const active = filterStatus === chip.key;
              return (
                <div key={chip.key} onClick={() => handleStatFilter(active ? '' : chip.key)}
                  style={{ background: active ? 'rgba(0,65,194,.1)' : 'var(--ds-card)', border: active ? '1px solid #0041C2' : '1px solid var(--ds-border)', borderRadius: 6, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', boxShadow: 'var(--ds-shadow)', transition: 'all .18s' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: chip.dot, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: 'var(--ds-text2)', fontWeight: 500 }}>{chip.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: active ? '#0041C2' : 'var(--ds-text)' }}>{chip.count}</span>
                </div>
              );
            })}
            <div style={{ width: 1, height: 18, background: 'var(--ds-border)', flexShrink: 0 }} />
            {/* No response */}
            {(() => {
              const active = filterStatus === 'Not Responded';
              return (
                <div onClick={() => handleStatFilter(active ? '' : 'Not Responded')}
                  style={{ background: active ? 'rgba(0,65,194,.1)' : 'var(--ds-card)', border: active ? '1px solid #0041C2' : '1px solid var(--ds-border)', borderRadius: 6, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', boxShadow: 'var(--ds-shadow)', transition: 'all .18s' }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#8A96B0', flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: 'var(--ds-text2)', fontWeight: 500 }}>No Response</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: active ? '#0041C2' : 'var(--ds-text)' }}>{statCounts['Not Responded']}</span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 20px', flexShrink: 0, flexWrap: 'wrap', background: 'var(--ds-bg)' }}>
          {filterStatus === 'Not Responded' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12, color: 'var(--ds-text2)', userSelect: 'none', flexShrink: 0 }}>
              <input type="checkbox" style={{ width: 14, height: 14, cursor: 'pointer' }}
                checked={selectedCandids.size > 0 && selectedCandids.size >= filtered.length}
                onChange={() => {}}
                onClick={toggleSelectAll}
              />
              Select All
            </label>
          )}
          {selectedCandids.size > 0 && (
            <button
              disabled={bulkSending}
              onClick={handleBulkResend}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, background: '#0041C2', border: 'none', color: '#fff', fontSize: 12, fontFamily: 'inherit', fontWeight: 600, cursor: bulkSending ? 'not-allowed' : 'pointer', opacity: bulkSending ? 0.7 : 1 }}
            >
              {bulkSending
                ? <span className="spinner-border spinner-border-sm" role="status" style={{ width: 11, height: 11 }} />
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              }
              Resend to {selectedCandids.size}
            </button>
          )}
          <div style={{ flex: 1, fontSize: 12, color: 'var(--ds-text2)' }}>
            <strong style={{ color: 'var(--ds-text)' }}>{filtered.length}</strong> candidates
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={selectStyle}>
            <option value="recent">Most Recent</option>
            <option value="name">Name A to Z</option>
            <option value="status">By Status</option>
          </select>
        </div>

        {error && (
          <div style={{ margin: '0 28px 8px', padding: '10px 14px', background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Candidate list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '3px 20px 8px', background: 'var(--ds-bg)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ds-text2)', fontSize: 13 }}>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading candidates...
            </div>
          ) : pageRows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ds-text2)', fontSize: 13 }}>
              {filterPosition ? 'No candidates for this position.' : 'No candidates found.'}
            </div>
          ) : (
            pageRows.map((cand) => {
              const isActive = cand.id === selId;
              const sc       = STATUS_CFG[cand.status] || STATUS_CFG['Not Responded'];
              const avColor  = getAvatarColor(cand.name);
              const initials = getInitials(cand.name);
              return (
                <div
                  key={cand.id}
                  onClick={() => handleChatOpen(cand)}
                  style={{
                    background: 'var(--ds-card)',
                    border: isActive ? '1px solid #0041C2' : '1px solid var(--ds-border)',
                    borderRadius: 7, padding: '6px 10px', marginTop: 3,
                    cursor: 'pointer', transition: 'all .2s',
                    display: 'flex', alignItems: 'center', gap: 8,
                    position: 'relative', overflow: 'hidden',
                    boxShadow: isActive ? '0 0 0 2px rgba(0,65,194,.1), var(--ds-shadow-md)' : 'var(--ds-shadow)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.transform = 'translateX(2px)';
                      e.currentTarget.style.boxShadow = 'var(--ds-shadow-md)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = isActive ? '0 0 0 2px rgba(0,65,194,.1), var(--ds-shadow-md)' : 'var(--ds-shadow)';
                  }}
                >
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
                    borderRadius: '3px 0 0 3px',
                    background: isActive ? 'linear-gradient(135deg,#0041C2,#1A5FD4)' : 'transparent',
                    transition: 'background .2s',
                  }} />
                  {filterStatus === 'Not Responded' && (
                    <input type="checkbox" checked={selectedCandids.has(cand.id)}
                      onChange={() => {}}
                      onClick={(e) => { e.stopPropagation(); toggleSelectCand(cand.id); }}
                      style={{ width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }}
                    />
                  )}
                  <div style={{ width: 30, height: 30, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#fff', background: avColor, position: 'relative' }}>
                    {initials}
                    <div style={{ position: 'absolute', bottom: 0, right: 0, width: 8, height: 8, borderRadius: '50%', border: '2px solid var(--ds-card)', background: sc.online ? '#10B981' : '#C8D0E0' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, minWidth: 0 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ds-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '55%' }}>{cand.name}</span>
                      <span style={{ fontSize: 10, color: 'var(--ds-text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 1 }}>· {cand.role}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
                      <StatusBadge status={cand.status} small />
                      {cand.shortlistStatus && (
                        <StatusBadge status={cand.shortlistStatus === 'shortlisted' ? 'Shortlisted' : 'Not Shortlisted'} small />
                      )}
                      {cand.criteriaRedFlag && (
                        <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: 'rgba(220,38,38,.12)', color: '#DC2626', border: '1px solid rgba(220,38,38,.3)' }}>
                          Flag
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: 'var(--ds-muted2)' }}>{fmtDateTime(cand.lastContacted)}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 4, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      title="View Chat"
                      style={iconActionBtn}
                      onClick={() => handleChatOpen(cand)}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1A5FD4'; e.currentTarget.style.background = 'rgba(0,65,194,.1)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.background = 'transparent'; }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="1.6" strokeLinecap="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            padding: '5px 20px', flexShrink: 0, borderTop: '1px solid var(--ds-border)',
            background: 'var(--ds-bg)', maxWidth: '100%', overflow: 'hidden',
          }}>
            <button
              disabled={safePage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid var(--ds-border)', background: 'transparent', cursor: safePage === 1 ? 'default' : 'pointer', opacity: safePage === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
            </button>
            {getPaginationItems(totalPages, safePage).map((item, idx) => (
              item === 'ellipsis' ? (
                <span
                  key={`ellipsis-${idx}`}
                  style={{
                    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14, color: 'var(--ds-muted)', flexShrink: 0, userSelect: 'none',
                  }}
                >
                  …
                </span>
              ) : (
                <button
                  key={item}
                  onClick={() => setCurrentPage(item)}
                  style={{
                    width: 28, height: 28, borderRadius: 7, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'inherit', padding: 0, flexShrink: 0,
                    border: item === safePage ? 'none' : '1px solid var(--ds-border)',
                    background: item === safePage ? 'linear-gradient(135deg,#0041C2,#1A5FD4)' : 'transparent',
                    color: item === safePage ? '#fff' : 'var(--ds-text2)',
                  }}
                >
                  {item}
                </button>
              )
            ))}
            <button
              disabled={safePage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid var(--ds-border)', background: 'transparent', cursor: safePage === totalPages ? 'default' : 'pointer', opacity: safePage === totalPages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
            </button>
          </div>
        )}
      </div>

      {/* ── Right panel: chat ── */}
      <div style={{
        flex: chatOpen ? 1 : 0,
        minWidth: chatOpen ? 420 : 0,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        transition: 'flex 0.35s cubic-bezier(.4,0,.2,1), min-width 0.35s cubic-bezier(.4,0,.2,1)',
        background: 'var(--ds-bg)',
        boxShadow: chatOpen ? '-6px 0 32px rgba(0,0,0,0.09)' : 'none',
        padding: chatOpen ? 12 : 0,
        boxSizing: 'border-box',
      }}>
        {chatOpen && selCand && (
          <div style={{
            display: 'flex', flexDirection: 'column', flex: 1,
            border: '1.5px solid var(--ds-border)',
            borderRadius: 16,
            overflow: 'hidden',
            background: 'var(--ds-card)',
            boxShadow: 'var(--ds-shadow-md)',
          }}>

            {/* Chat header */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--ds-card)', borderRadius: '16px 16px 0 0' }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0, background: getAvatarColor(selCand.name) }}>
                {getInitials(selCand.name)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ds-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selCand.name}</div>
                <div style={{ fontSize: 11, color: 'var(--ds-text2)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{selCand.role}</span><span>·</span>
                  <StatusBadge status={selCand.status} small />
                  {selCand.shortlistStatus && (
                    <StatusBadge status={selCand.shortlistStatus === 'shortlisted' ? 'Shortlisted' : 'Not Shortlisted'} small />
                  )}
                  {(selCand.criteriaRedFlag || selCand.criteria_red_flag) && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999, background: 'rgba(220,38,38,.12)', color: '#DC2626', border: '1px solid rgba(220,38,38,.35)' }}>
                      Criteria flag
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexShrink: 0 }}>
                <button title="Call" style={chatHdrBtn} onClick={() => toast.info(`Calling ${selCand.name}...`)}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1A5FD4'; e.currentTarget.style.background = 'var(--ds-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.background = 'transparent'; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="1.7" strokeLinecap="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.4 1.13 2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                </button>
                <button title="View Profile" style={chatHdrBtn} onClick={() => toast.info(`Opening ${selCand.name} profile...`)}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1A5FD4'; e.currentTarget.style.background = 'var(--ds-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.background = 'transparent'; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="1.7" strokeLinecap="round">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  </svg>
                </button>
                <button
                  title="Refresh chat"
                  style={chatHdrBtn}
                  onClick={() => fetchChatSilent(true)}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1A5FD4'; e.currentTarget.style.background = 'var(--ds-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.background = 'transparent'; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="1.8" strokeLinecap="round"
                    style={{ animation: chatRefreshing ? 'spin 0.7s linear infinite' : 'none' }}>
                    <polyline points="23 4 23 10 17 10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                </button>
                <button title="Close chat" style={chatHdrBtn} onClick={handleCloseChat}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#EF4444'; e.currentTarget.style.background = 'rgba(239,68,68,.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.background = 'transparent'; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ds-text2)" strokeWidth="2" strokeLinecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Info strip */}
            <div style={{ padding: '10px 16px', background: 'var(--ds-card2)', borderBottom: '1px solid var(--ds-border)', display: 'flex', gap: 20, flexShrink: 0, flexWrap: 'wrap' }}>
              {[
                { label: 'Phone',        value: selCand.phoneNumber || '-' },
                { label: 'Position',     value: selCand.role },
                { label: 'Last Contact', value: fmtDateTime(selCand.lastContacted) },
                ...(selCand.criteriaRedFlag ? [{
                  label: 'Criteria',
                  value: `Red flag (${selCand.criteriaNoCount ?? '?'}/${selCand.criteriaTotalCount ?? '?'} NO)`,
                }] : []),
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--ds-muted2)', fontWeight: 600, marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ds-text2)' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Messages */}
            <div ref={chatScrollRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', gap: 12, background: 'var(--ds-input-bg)' }}>
              {chatLoading ? (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--ds-text2)', fontSize: 13 }}>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Loading conversation...
                </div>
              ) : chatError ? (
                <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#dc2626', fontSize: 12 }}>
                  {chatError}
                </div>
              ) : chatMsgs.length === 0 ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 32 }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--ds-muted2)" strokeWidth="1.5" strokeLinecap="round" style={{ marginBottom: 12 }}>
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ds-text)', marginBottom: 4 }}>No messages yet</div>
                  <div style={{ fontSize: 12, color: 'var(--ds-text2)' }}>No conversation history for this candidate.</div>
                </div>
              ) : (
                chatMsgs.map((msg, idx) => {
                  const isIn = msg.direction === 'incoming';
                  return (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: isIn ? 'flex-start' : 'flex-end' }}>
                      {isIn && (
                        <div style={{ fontSize: 10, color: 'var(--ds-text2)', paddingLeft: 4 }}>
                          {selCand.name.split(' ')[0]}
                        </div>
                      )}
                      <div style={{
                        maxWidth: '78%', padding: '10px 14px', wordBreak: 'break-word',
                        borderRadius: isIn ? '14px 14px 14px 4px' : '14px 14px 4px 14px',
                        fontSize: 13, lineHeight: 1.55,
                        background: isIn ? 'var(--ds-card)' : '#0041C2',
                        color: isIn ? 'var(--ds-text)' : '#fff',
                        border: isIn ? '1px solid var(--ds-border)' : 'none',
                      }}>
                        {msg.text}
                        {!isIn && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.7)" strokeWidth="2.5" strokeLinecap="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          </div>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--ds-text2)', paddingLeft: 4, paddingRight: 4 }}>
                        {fmtTime(msg.time)}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input area */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--ds-border)', background: 'var(--ds-card)', flexShrink: 0, borderRadius: '0 0 16px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <textarea
                  placeholder="Type a message..."
                  rows={1}
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); } }}
                  style={{
                    flex: 1, background: 'var(--ds-input-bg)', border: '1px solid var(--ds-border)',
                    borderRadius: 12, padding: '10px 14px', fontSize: 13, fontFamily: 'inherit',
                    color: 'var(--ds-text)', outline: 'none', resize: 'none', maxHeight: 100, lineHeight: 1.5,
                  }}
                  onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px'; }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#0041C2'; }}
                  onBlur={(e)  => { e.currentTarget.style.borderColor = 'var(--ds-border)'; }}
                />
                <button
                  disabled={!chatInput.trim() || sendingChat}
                  onClick={handleChatSend}
                  style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: (!chatInput.trim() || sendingChat) ? 'not-allowed' : 'pointer', flexShrink: 0, boxShadow: '0 0 10px rgba(0,65,194,.25)', opacity: (!chatInput.trim() || sendingChat) ? 0.6 : 1 }}
                  onMouseEnter={(e) => { if (chatInput.trim() && !sendingChat) { e.currentTarget.style.boxShadow = '0 0 18px rgba(0,65,194,.45)'; e.currentTarget.style.transform = 'scale(1.05)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 0 10px rgba(0,65,194,.25)'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {sendingChat
                    ? <span className="spinner-border spinner-border-sm" role="status" style={{ width: 14, height: 14, borderColor: 'rgba(255,255,255,.5)', borderTopColor: '#fff' }} />
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  }
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button style={chatActBtn} onClick={() => toast.info('Resending WhatsApp message...')}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1A5FD4'; e.currentTarget.style.color = '#1A5FD4'; e.currentTarget.style.background = 'var(--ds-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.color = 'var(--ds-text2)'; e.currentTarget.style.background = 'transparent'; }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.4 1.13 2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                  </svg>
                  Resend WhatsApp
                </button>
                <button
                  disabled={updatingStatus}
                  style={{ ...chatActBtn, opacity: updatingStatus ? 0.6 : 1,
                    ...(selCand.shortlistStatus === 'shortlisted' ? { borderColor: '#7C3AED', color: '#7C3AED', background: 'rgba(124,58,237,.12)', fontWeight: 700 } : {})
                  }}
                  onClick={() => handleStatusUpdate('shortlisted')}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#7C3AED'; e.currentTarget.style.color = '#7C3AED'; e.currentTarget.style.background = 'rgba(124,58,237,.08)'; }}
                  onMouseLeave={(e) => {
                    if (selCand.shortlistStatus === 'shortlisted') { e.currentTarget.style.borderColor = '#7C3AED'; e.currentTarget.style.color = '#7C3AED'; e.currentTarget.style.background = 'rgba(124,58,237,.12)'; }
                    else { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.color = 'var(--ds-text2)'; e.currentTarget.style.background = 'transparent'; }
                  }}>
                  {updatingStatus
                    ? <span className="spinner-border spinner-border-sm" role="status" style={{ width: 10, height: 10 }} />
                    : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  }
                  {selCand.shortlistStatus === 'shortlisted' ? '✓ Shortlisted' : 'Mark Shortlisted'}
                </button>
                <button
                  disabled={updatingStatus}
                  style={{ ...chatActBtn, opacity: updatingStatus ? 0.6 : 1,
                    ...(selCand.shortlistStatus === 'not-shortlisted' ? { borderColor: '#EF4444', color: '#EF4444', background: 'rgba(239,68,68,.1)', fontWeight: 700 } : {})
                  }}
                  onClick={() => handleStatusUpdate('not-shortlisted')}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#EF4444'; e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.background = 'rgba(239,68,68,.08)'; }}
                  onMouseLeave={(e) => {
                    if (selCand.shortlistStatus === 'not-shortlisted') { e.currentTarget.style.borderColor = '#EF4444'; e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.background = 'rgba(239,68,68,.1)'; }
                    else { e.currentTarget.style.borderColor = 'var(--ds-border)'; e.currentTarget.style.color = 'var(--ds-text2)'; e.currentTarget.style.background = 'transparent'; }
                  }}>
                  {updatingStatus
                    ? <span className="spinner-border spinner-border-sm" role="status" style={{ width: 10, height: 10 }} />
                    : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  }
                  {selCand.shortlistStatus === 'not-shortlisted' ? '✗ Not Shortlisted' : 'Not Shortlisted'}
                </button>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default CandidateEngagement;
